import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Headphones,
  MessageCircle,
  Rocket,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../supabase';
import GastifyBrand from '../components/public/GastifyBrand';
import { hasPublishedDemoVideos, whatsappHref } from '../data/publicSite';
import '../components/public/publicSite.css';

function PedirDemo() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    rubro: '',
    nombreNegocio: '',
  });
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.from('demo_requests').insert([
        {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          nombre_negocio: formData.nombreNegocio,
          rubro: formData.rubro,
        },
      ]);

      if (error) throw error;

      const { error: notifyError } = await supabase.functions.invoke('send-demo-request-email', {
        body: {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          nombre_negocio: formData.nombreNegocio,
          rubro: formData.rubro,
        },
      });

      if (notifyError) {
        console.error('No se pudo enviar la notificación por email de la demo:', notifyError);
      }

      setEnviado(true);
    } catch (error) {
      alert(`Hubo un error al enviar la solicitud: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gst-site-tokens min-h-screen overflow-hidden bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700;9..144,800&family=Manrope:wght@400;500;600;700;800&display=swap');
        .gst-public-display { font-family: 'Fraunces', Georgia, serif; }
        .gst-public-body { font-family: 'Manrope', system-ui, sans-serif; }
      `}</style>

      <div className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(254,215,170,0.34),_transparent_32%)]" />

      <div className="relative z-10 min-h-screen px-4 py-5 md:px-8">
        <header className="gst-public-body mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="shrink-0">
            <GastifyBrand subtitle="Demo guiada según la operación" />
          </Link>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-2 text-sm font-bold text-[var(--gst-graphite-900)] transition hover:border-[var(--gst-mint-300)] hover:text-[var(--gst-mint-700)]"
          >
            <ArrowLeft size={16} />
            Volver
          </button>
        </header>

        <main className="gst-public-body mx-auto mt-6 grid max-w-7xl gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
          <section className="flex flex-col justify-between rounded-[2rem] border border-[var(--gst-border)] bg-[var(--gst-graphite-900)] p-8 text-white shadow-[0_24px_50px_rgba(31,36,33,0.18)] md:p-10">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[var(--surface)]/12 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-200)]">
                <Sparkles size={13} />
                Demo comercial
              </div>
              <h1 className="gst-public-display mt-5 text-4xl font-black leading-[1] tracking-[-0.04em] sm:text-5xl md:text-6xl">
                Una demo en vivo,
                <br />
                sobre tu negocio,
                <br />
                en 20 minutos.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/88 md:text-lg">
                Pensada para operaciones con varias sucursales, producción o mucho volumen. La armamos sobre tu rubro,
                tu circuito de ventas y el control que te falta hoy.
              </p>
              {hasPublishedDemoVideos() && (
                <Link
                  to="/demo"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.14em] text-[var(--gst-mint-200)] transition hover:gap-3"
                >
                  ¿Querés verlo ya mismo? Mirá los videos demo <ArrowRight size={15} />
                </Link>
              )}
            </div>

            <div className="mt-8 space-y-4">
              <article className="rounded-[1.6rem] border border-white/15 bg-[var(--surface)]/12 p-5">
                <div className="flex items-center gap-3">
                  <Rocket size={19} className="text-[var(--gst-mint-200)]" />
                  <h2 className="text-lg font-black text-white">Implementación enfocada</h2>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-white/84">
                  El arranque se construye alrededor del mostrador, el stock y los puntos de control del negocio.
                </p>
              </article>

              <article className="rounded-[1.6rem] border border-white/15 bg-[var(--surface)]/12 p-5">
                <div className="flex items-center gap-3">
                  <Headphones size={19} className="text-[var(--gst-mint-200)]" />
                  <h2 className="text-lg font-black text-white">Acompañamiento humano</h2>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-white/84">
                  El enfoque se mantiene en idioma de negocio: ventas, caja, reposición, producción o margen.
                </p>
              </article>

              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gst-mint-500)] px-5 py-3.5 text-sm font-black uppercase tracking-[0.12em] text-[var(--text-default)] transition hover:bg-[var(--gst-mint-600)]"
              >
                <MessageCircle size={16} />
                WhatsApp comercial
              </a>
            </div>
          </section>

          <section className="rounded-[2rem] border border-[var(--gst-border-strong)] bg-[var(--surface)] p-8 shadow-[0_18px_40px_rgba(31,36,33,0.08)] md:p-10">
            {!enviado ? (
              <>
                <div className="mb-7">
                  <div className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-graphite-700)]">Solicitar demo</div>
                  <h2 className="gst-public-display mt-3 text-3xl font-black leading-tight text-[var(--gst-graphite-900)] sm:text-4xl">
                    Contanos cómo es la operación.
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--gst-graphite-700)]">
                    Así la demostración se arma de forma alineada al rubro y al nivel de control que hace falta sumar.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)]">
                      Nombre completo
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-3.5 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]"
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)]">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-3.5 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)]">
                      Teléfono o WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-3.5 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]"
                      placeholder="Código de área + número"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)]">
                        Nombre del negocio
                      </label>
                      <input
                        type="text"
                        name="nombreNegocio"
                        value={formData.nombreNegocio}
                        onChange={handleChange}
                        required
                        className="w-full rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-3.5 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]"
                        placeholder="Ej: Mercado Central"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)]">
                        Rubro
                      </label>
                      <select
                        name="rubro"
                        value={formData.rubro}
                        onChange={handleChange}
                        required
                        className="w-full rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-3.5 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]"
                      >
                        <option value="" disabled>
                          Seleccionar...
                        </option>
                        <option value="kiosco">Kiosco / almacén</option>
                        <option value="indumentaria">Indumentaria</option>
                        <option value="gastronomia">Gastronomía</option>
                        <option value="servicios">Servicios</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-black uppercase tracking-[0.12em] transition ${
                      loading
                        ? 'cursor-not-allowed bg-[var(--gst-mint-300)] text-white/80'
                        : 'bg-[var(--gst-mint-500)] text-white hover:bg-[var(--gst-mint-600)]'
                    }`}
                  >
                    {loading ? 'Enviando...' : 'Pedir demo'}
                  </button>
                </form>
              </>
            ) : (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]">
                  <CheckCircle2 size={42} />
                </div>
                <h2 className="gst-public-display mt-6 text-3xl font-black leading-tight text-[var(--gst-graphite-900)] sm:text-4xl">
                  Solicitud recibida.
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-[var(--gst-graphite-700)]">
                  Gracias, <strong>{formData.nombre}</strong>. Vamos a contactar a <strong>{formData.telefono}</strong> para preparar una demo alineada a <strong>{formData.nombreNegocio}</strong>.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-6 inline-flex items-center justify-center rounded-full bg-[var(--gst-graphite-900)] px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-[var(--text-default)] transition hover:bg-[var(--gst-mint-700)]"
                >
                  Volver al inicio
                </button>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

export default PedirDemo;
